import { GET_ACTIVITY_EXPRESSION_VARIABLES } from '@shared/constants/activities.constants';
import { isOneOf } from '@shared/helpers/array.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { getKeys } from '@shared/helpers/object.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { emptyStoreySummaryVariables } from '@shared/models/design/design-expression-variables.model';

const expressionFunctions = [
  'getStorey',
  'getBuildings',
  'getActivity',
  'isActivity',
  'getProductDensity',
  'getProductDensityPerSqm',
] as const;

type ExpressionFunctionName = (typeof expressionFunctions)[number];

const GET_STOREY_PROPERTY_KEYS = getKeys(emptyStoreySummaryVariables);
const GET_ACTIVITY_TYPES = getKeys(GET_ACTIVITY_EXPRESSION_VARIABLES);

/** Extra keys on a `getBuildings(...)` result besides storey summary fields. */
const GET_BUILDINGS_OWN_PROPERTY_KEYS = [
  'name',
  'storeys_count',
  'getStorey',
] as const;

const FUNCTION_CALL_SUFFIX_PATTERN = new RegExp(
  `(${expressionFunctions.join('|')})\\(([^)]*)\\)\\.$`,
);

/** `getBuildings(args).getStorey(args).` at the end of the typed expression. */
const GET_BUILDINGS_GET_STOREY_SUFFIX_PATTERN =
  /getBuildings\(([^)]*)\)\.getStorey\(([^)]*)\)\.$/;

/** Match `parent.` / `parent.parent.` immediately before a function name, not inside identifiers like `apparent.`. */
const PARENT_PREFIX_PATTERN = /(?:^|[^A-Za-z0-9_])((?:parent\.)+)$/;

export type ExpressionFunctionPropertyContext = {
  /** Nested `parent` hops (`parent.getStorey().` → 1, `parent.parent.getStorey().` → 2). */
  parentHops: number;
  functionName: ExpressionFunctionName;
  args: Array<string | number>;
  /**
   * When completing `getBuildings(i).getStorey(n).`, the `getBuildings` call
   * arguments so preview uses the nested `getStorey` on that selection.
   */
  buildingsArgs?: Array<string | number>;
};

/**
 * Parse a single argument from an expression function call (number, quoted string, or name).
 */
const parseExpressionFunctionArg = (
  raw: string,
): string | number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const quotedMatch = trimmed.match(/^(['"])(.*)\1$/);
  if (quotedMatch) return quotedMatch[2];

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);

  return trimmed;
};

/**
 * Parse comma-separated expression function arguments.
 */
const parseExpressionFunctionArgs = (
  argsSource: string,
): Array<string | number> => {
  if (!argsSource.trim()) return [];

  return argsSource
    .split(',')
    .map(parseExpressionFunctionArg)
    .filter((arg): arg is string | number => arg !== undefined);
};

/**
 * Parse `fn(args).` property-completion context at the end of an expression.
 * Optional `parent.` / `parent.parent.` prefixes count as hops into nested `parent` scopes
 * used by {@link isSuggestedExpressionFunctionProperty} and {@link getVariableValueForExpressionSelectPreview}.
 */
export const getExpressionFunctionPropertyContext = (
  contextBeforeProperty: string,
): ExpressionFunctionPropertyContext | undefined => {
  const nestedMatch = contextBeforeProperty.match(
    GET_BUILDINGS_GET_STOREY_SUFFIX_PATTERN,
  );
  if (nestedMatch && nestedMatch.index !== undefined) {
    const beforeFunction = contextBeforeProperty.slice(0, nestedMatch.index);
    const parentPrefix = beforeFunction.match(PARENT_PREFIX_PATTERN)?.[1] ?? '';
    return {
      parentHops: parentPrefix
        .split('.')
        .filter((segment) => segment === 'parent').length,
      functionName: 'getStorey',
      args: parseExpressionFunctionArgs(nestedMatch[2] ?? ''),
      buildingsArgs: parseExpressionFunctionArgs(nestedMatch[1] ?? ''),
    };
  }

  const match = contextBeforeProperty.match(FUNCTION_CALL_SUFFIX_PATTERN);
  if (!match || match.index === undefined) return undefined;

  const functionName = match[1];
  if (!isOneOf(expressionFunctions, functionName)) return undefined;

  const beforeFunction = contextBeforeProperty.slice(0, match.index);
  const parentPrefix = beforeFunction.match(PARENT_PREFIX_PATTERN)?.[1] ?? '';

  return {
    parentHops: parentPrefix
      .split('.')
      .filter((segment) => segment === 'parent').length,
    functionName,
    args: parseExpressionFunctionArgs(match[2] ?? ''),
  };
};

/**
 * Walk `variables.parent` for the given number of hops.
 */
const getVariablesScopeAtParentHops = (
  variables: Record<string, unknown>,
  parentHops: number,
): Record<string, unknown> | undefined => {
  let scope: unknown = variables;
  for (let hop = 0; hop < parentHops; hop++) {
    if (!guards.is.object(scope)) return undefined;
    scope = scope.parent;
  }
  return guards.is.object(scope) ? scope : undefined;
};

/**
 * Invoke an expression function from variables with parsed call arguments.
 */
const invokeExpressionFunction = (
  variables: Record<string, unknown>,
  functionName: ExpressionFunctionName,
  args: Array<string | number>,
): unknown => {
  const fn = variables[functionName];
  if (!guards.is.function(fn)) return undefined;
  return fn(...args);
};

/**
 * Resolve a property on the record returned by `getStorey` / `getActivity` in the given parent scope.
 */
const getPropertyValueFromFunctionContext = (
  variables: Record<string, unknown>,
  propertyKey: string,
  context: ExpressionFunctionPropertyContext,
): unknown => {
  const scope = getVariablesScopeAtParentHops(variables, context.parentHops);
  if (!scope) return undefined;
  if (context.buildingsArgs) {
    const buildingsResult = invokeExpressionFunction(
      scope,
      'getBuildings',
      context.buildingsArgs,
    );
    if (!guards.is.object(buildingsResult)) return undefined;
    const getStorey = buildingsResult.getStorey;
    if (!guards.is.function(getStorey)) return undefined;
    const storeyResult = getStorey(...context.args);
    if (!guards.is.object(storeyResult)) return undefined;
    return storeyResult[propertyKey];
  }
  const result = invokeExpressionFunction(
    scope,
    context.functionName,
    context.args,
  );
  if (!guards.is.object(result)) return undefined;
  return result[propertyKey];
};

/**
 * Whether `variable` is a valid property suggestion after `getStorey(...)` / `getActivity(...)`.
 * `parent.getStorey(...)` and `parent.parent.getStorey(...)` use the same property list as `getStorey(...)`.
 */
export const isSuggestedExpressionFunctionProperty = (
  contextBeforeProperty: string,
  variable: string,
): boolean => {
  const context = getExpressionFunctionPropertyContext(contextBeforeProperty);
  if (!context) return false;

  switch (context.functionName) {
    case 'getStorey':
      return isOneOf(GET_STOREY_PROPERTY_KEYS, variable);
    case 'getBuildings':
      return (
        isOneOf(GET_STOREY_PROPERTY_KEYS, variable) ||
        isOneOf(GET_BUILDINGS_OWN_PROPERTY_KEYS, variable)
      );
    case 'getActivity': {
      const activityType = context.args[0];
      if (!isOneOf(GET_ACTIVITY_TYPES, activityType)) return false;
      return isOneOf(
        getKeys(GET_ACTIVITY_EXPRESSION_VARIABLES[activityType]),
        variable,
      );
    }
    case 'isActivity':
    case 'getProductDensity':
    case 'getProductDensityPerSqm':
      return false;
    default:
      return assertUnreachable(context.functionName);
  }
};

/**
 * Resolve a variable preview value for the expression autocomplete list.
 * Handles function property context (`getStorey(1).gfa`, `parent.getStorey(1).gfa`) and zero-arg calls (`getStorey()`).
 * When the text is a function-property context, missing scopes or properties return undefined instead of the top-level key.
 */
export const getVariableValueForExpressionSelectPreview = (
  variables: Record<string, unknown>,
  displayKey: string,
  contextBeforeProperty?: string,
): unknown => {
  if (contextBeforeProperty) {
    const context = getExpressionFunctionPropertyContext(contextBeforeProperty);
    if (context) {
      return getPropertyValueFromFunctionContext(
        variables,
        displayKey,
        context,
      );
    }
  }

  if (displayKey.endsWith('()')) {
    const functionName = displayKey.slice(0, -2);
    if (isOneOf(expressionFunctions, functionName)) {
      return invokeExpressionFunction(variables, functionName, []);
    }
  }

  return getVariableValueFromDisplayKey(variables, displayKey);
};

/**
 * Collect suggestable variable keys for nested objects and arrays.
 * Arrays use 1-based indexes (e.g. storeys[1].gfa) to match math.js.
 * `getStorey(n)` uses the same 1-based convention (`getStorey(1)` is the first storey).
 *
 * @param variables - Expression variables record (may contain nested objects and arrays like programs, storeys)
 * @returns Additional keys to suggest in autocomplete (dotted and bracket form)
 */
export const getNestedExpressionVariableKeys = (
  variables: Record<string, unknown>,
): string[] => {
  const keys: string[] = [];
  for (const [parentKey, parentValue] of Object.entries(variables)) {
    if (guards.is.array(parentValue)) {
      for (let i = 1; i <= parentValue.length; i++) {
        keys.push(`${parentKey}[${i}]`);
        const element = parentValue[i - 1];
        if (!guards.is.object(element)) continue;
        for (const childKey of Object.keys(element)) {
          keys.push(`${parentKey}[${i}].${childKey}`);
        }
      }
      continue;
    }
    if (!guards.is.object(parentValue)) continue;
    for (const childKey of Object.keys(parentValue)) {
      const dotted = `${parentKey}.${childKey}`;
      keys.push(dotted);
      const childValue = parentValue[childKey];
      if (!guards.is.object(childValue)) continue;
      for (const grandChildKey of Object.keys(childValue)) {
        keys.push(`${dotted}.${grandChildKey}`);
      }
    }
  }
  return keys;
};

/**
 * Resolve a variable value from the display key (e.g. "getStorey()", "programs.stairwells.gfa", "storeys[1].gfa").
 * Strips "()" for function keys; supports dotted paths and 1-based bracket indexes (math.js convention).
 *
 * @param variables - Expression variables record
 * @param displayKey - Key as shown in the list
 * @returns The value at that path, or undefined
 */
export const getVariableValueFromDisplayKey = (
  variables: Record<string, unknown>,
  displayKey: string,
): unknown => {
  const lookupKey = displayKey.endsWith('()')
    ? displayKey.slice(0, -2)
    : displayKey;
  if (!lookupKey.includes('.') && !lookupKey.includes('[')) {
    return variables[lookupKey];
  }
  const segments = lookupKey.split('.');
  return segments.reduce<unknown>((obj, segment) => {
    if (!guards.is.defined(obj) || !guards.is.object(obj)) return undefined;
    const bracketMatch = segment.match(/^(.+)\[(\d+)\]$/);
    if (bracketMatch) {
      const key = bracketMatch[1];
      const indexStr = bracketMatch[2];
      if (key === undefined || indexStr === undefined) return undefined;
      const oneBasedIndex = parseInt(indexStr, 10);
      const arr = obj[key];
      if (!guards.is.array(arr)) return undefined;
      return arr[oneBasedIndex - 1];
    }
    return obj[segment];
  }, variables);
};

/**
 * Get Expression Variable keys with parentheses after 'get' and 'is' functions
 * @param expressionVariableKeys
 * @returns
 */
export const getExpressionVariableKeysWithParenthesesAfterGetAndIsFunctions = (
  expressionVariableKeys: string[],
): string[] =>
  expressionVariableKeys.map((key) =>
    isOneOf(expressionFunctions, key) ? `${key}()` : key,
  );
